import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, updateDoc, getDoc, setDoc, collection, getDocs, deleteDoc, addDoc } from 'firebase/firestore';
import { Container, Form, Button, Row, Col, Card, Table, Tabs, Tab, InputGroup } from 'react-bootstrap';
import { Save, LogOut, Plus, Trash2, Database, UserPlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const diasDaSemana = [
  "segunda-feira", "terça-feira", "quarta-feira",
  "quinta-feira", "sexta-feira", "sábado", "domingo"
];

async function hashPassword(password) {
  const msgBuffer = new TextEncoder().encode(password);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

const Admin = () => {
  const navigate = useNavigate();

  const [nomeEmpresa, setNomeEmpresa] = useState("");
  const [sobre, setSobre] = useState("");
  const [endereco, setEndereco] = useState("");
  const [horarios, setHorarios] = useState({});
  const [planos, setPlanos] = useState([]);
  const [admins, setAdmins] = useState([]);

  const [novoEmailAdmin, setNovoEmailAdmin] = useState("");
  const [senhaAdmin, setSenhaAdmin] = useState("");

  // Contato da academia
  const [whatsapp, setWhatsapp] = useState("");
  const [instagram, setInstagram] = useState("");
  const [emailContato, setEmailContato] = useState("");

  useEffect(() => {
    document.title = "Academia Ativa";
    if (!localStorage.getItem('adminAuth')) {
      navigate('/area-interna-gestao');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const res = await getDoc(doc(db, "configuracoes", "geral"));
      if (res.exists()) {
        const data = res.data();
        setNomeEmpresa(data.nome || "");
        setSobre(data.sobre || "");
        setEndereco(data.endereco || "");
        setHorarios(data.horariosEstruturados || {});
        setPlanos(data.planos || []);
        setWhatsapp(data.whatsapp || "");
        setInstagram(data.instagram || "");
        setEmailContato(data.emailContato || "");
      }
      const querySnapshot = await getDocs(collection(db, "admins"));
      setAdmins(querySnapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Erro ao carregar dados:", err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminAuth');
    navigate('/area-interna-gestao');
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    if (!novoEmailAdmin || !senhaAdmin) return;
    try {
      const hashed = await hashPassword(senhaAdmin);
      await addDoc(collection(db, "admins"), {
        email: novoEmailAdmin.toLowerCase().trim(),
        password: hashed,
        createdAt: new Date()
      });
      setNovoEmailAdmin("");
      setSenhaAdmin("");
      fetchData();
      alert("Admin criado com sucesso!");
    } catch (err) {
      alert("Erro ao criar admin: " + err.message);
    }
  };

  const handleHorarioChange = (dia, campo, valor) => {
    setHorarios(prev => ({
      ...prev,
      [dia]: { ...prev[dia], [campo]: valor }
    }));
  };

  const addPlano = () => {
    setPlanos([...planos, { nome: "", preco: "", beneficios: "" }]);
  };

  const updatePlano = (index, campo, valor) => {
    const novosPlanos = [...planos];
    novosPlanos[index][campo] = valor;
    setPlanos(novosPlanos);
  };

  const removePlano = (index) => {
    setPlanos(planos.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    try {
      await updateDoc(doc(db, "configuracoes", "geral"), {
        nome: nomeEmpresa,
        sobre: sobre,
        endereco: endereco,
        whatsapp: whatsapp,
        instagram: instagram,
        emailContato: emailContato,
        horariosEstruturados: horarios,
        planos: planos
      });
      alert("Site atualizado com sucesso!");
    } catch (err) {
      alert("Erro ao salvar: " + err.message);
    }
  };

  const gerarDadosDemo = async () => {
    const demoData = {
      nome: "Minha Academia",
      sobre: "Sobre a academia...",
      endereco: "Rua Exemplo, 123",
      whatsapp: "73999999999",
      instagram: "sua_academia",
      emailContato: "contato@academia.com",
      horariosEstruturados: {},
      planos: [],
      carouselImages: [],
      sobreImage: "",
      instalacoesImages: []
    };
    await setDoc(doc(db, "configuracoes", "geral"), demoData);
    fetchData();
    alert("Documento 'geral' criado!");
  };

  return (
    <Container className="py-5 bg-light rounded shadow mt-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bold text-dark">Painel Administrativo</h2>
        <div>
          <Button variant="outline-secondary" className="me-2" onClick={gerarDadosDemo}>
            <Database size={18} /> Criar Doc Inicial
          </Button>
          <Button variant="danger" onClick={handleLogout}>
            <LogOut size={20} className="me-2" /> Sair
          </Button>
        </div>
      </div>

      <Tabs defaultActiveKey="geral" id="admin-tabs" className="mb-4">

        {/* ABA GERAL */}
        <Tab eventKey="geral" title="Informações Gerais">
          <Card className="p-4 border-0 shadow-sm">
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Nome da Academia</Form.Label>
              <Form.Control value={nomeEmpresa} onChange={e => setNomeEmpresa(e.target.value)} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Sobre a Empresa</Form.Label>
              <Form.Control as="textarea" rows={3} value={sobre} onChange={e => setSobre(e.target.value)} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Endereço</Form.Label>
              <Form.Control value={endereco} onChange={e => setEndereco(e.target.value)} />
            </Form.Group>

            <hr />
            <h5 className="fw-bold mb-3 text-warning" style={{ color: '#FF8C00' }}>Canais de Contato da Academia</h5>
            
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">WhatsApp (Apenas números com DDD)</Form.Label>
                  <Form.Control placeholder="Ex: 73999999999" value={whatsapp} onChange={e => setWhatsapp(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">Instagram (Apenas o usuário)</Form.Label>
                  <Form.Control placeholder="Ex: ativa_academia" value={instagram} onChange={e => setInstagram(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">E-mail de Atendimento</Form.Label>
                  <Form.Control type="email" placeholder="Ex: contato@academia.com" value={emailContato} onChange={e => setEmailContato(e.target.value)} />
                </Form.Group>
              </Col>
            </Row>
          </Card>
        </Tab>

        {/* ABA HORÁRIOS */}
        <Tab eventKey="horarios" title="Horários">
          <Card className="p-4 border-0 shadow-sm">
            {diasDaSemana.map(dia => (
              <Row key={dia} className="align-items-center mb-3">
                <Col sm={3} className="text-capitalize"><strong>{dia}:</strong></Col>
                <Col sm={4}>
                  <Form.Control type="time" value={horarios[dia]?.abertura || ""} onChange={(e) => handleHorarioChange(dia, "abertura", e.target.value)} />
                </Col>
                <Col sm={1} className="text-center">às</Col>
                <Col sm={4}>
                  <Form.Control type="time" value={horarios[dia]?.fechamento || ""} onChange={(e) => handleHorarioChange(dia, "fechamento", e.target.value)} />
                </Col>
              </Row>
            ))}
          </Card>
        </Tab>

        {/* ABA PLANOS */}
        <Tab eventKey="planos" title="Planos">
          <Card className="p-4 border-0 shadow-sm">
            {planos.map((plano, index) => (
              <div key={index} className="p-3 mb-3 border rounded bg-white">
                <Row>
                  <Col md={3}><Form.Control placeholder="Plano" value={plano.nome} onChange={e => updatePlano(index, 'nome', e.target.value)} /></Col>
                  <Col md={2}><Form.Control placeholder="Preço" value={plano.preco} onChange={e => updatePlano(index, 'preco', e.target.value)} /></Col>
                  <Col md={6}><Form.Control placeholder="Benefícios (ex: Musculação, Wi-Fi)" value={plano.beneficios} onChange={e => updatePlano(index, 'beneficios', e.target.value)} /></Col>
                  <Col md={1}><Button variant="outline-danger" onClick={() => removePlano(index)}><Trash2 size={18} /></Button></Col>
                </Row>
              </div>
            ))}
            <Button variant="dark" onClick={addPlano} className="mt-2"><Plus size={18} /> Adicionar Plano</Button>
          </Card>
        </Tab>

        {/* ABA ADMINS */}
        <Tab eventKey="admins" title="Equipe Admin">
          <Card className="p-4 border-0 shadow-sm">
            <h5 className="fw-bold mb-3">Autorizar Novo Administrador</h5>
            <Form onSubmit={handleAddAdmin} className="mb-4">
              <InputGroup>
                <Form.Control
                  type="email"
                  placeholder="E-mail do novo admin"
                  value={novoEmailAdmin}
                  onChange={(e) => setNovoEmailAdmin(e.target.value)}
                  required
                />
                <Form.Control
                  type="password"
                  placeholder="Senha do novo admin"
                  value={senhaAdmin}
                  onChange={(e) => setSenhaAdmin(e.target.value)}
                  required
                />
                <Button variant="warning" type="submit" style={{ backgroundColor: '#FF8C00', border: 'none' }}>
                  <UserPlus size={18} className="me-1" /> Autorizar
                </Button>
              </InputGroup>
            </Form>

            <h5 className="fw-bold mb-3">Administradores Cadastrados</h5>
            <Table hover responsive>
              <thead><tr><th>Email</th><th>Ações</th></tr></thead>
              <tbody>
                {admins.map(adm => (
                  <tr key={adm.id}>
                    <td>{adm.email}</td>
                    <td>
                      <Button variant="danger" size="sm" onClick={async () => {
                        if (window.confirm("Remover acesso deste admin?")) {
                          await deleteDoc(doc(db, "admins", adm.id));
                          fetchData();
                        }
                      }}>Remover</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </Tab>

      </Tabs>

      <div className="sticky-bottom bg-white p-3 border-top d-flex justify-content-end">
        <Button variant="warning" size="lg" className="fw-bold px-5" onClick={handleSave} style={{ backgroundColor: '#FF8C00', border: 'none' }}>
          <Save size={20} className="me-2" /> SALVAR TODAS AS ALTERAÇÕES
        </Button>
      </div>
    </Container>
  );
};

export default Admin;